import React, { useMemo } from "react";
import { useQuery } from "react-query";
import { getOMRefundInfo } from "../../../../../apis/OrderManagement/order";
import useReservation from "../../../../../hooks/useReservation";
import { CustomerOrderProductInfo } from "../../../../../model/OrderManagement/CustomerOrder";
import { OrderPackageInfo } from "../../../../../model/OrderManagement/Package";
import FormatFunction from "../../../../../shared/utils/Fn";
import { CustomDescriptions } from "../../../Description/index.styled";
import { getKeyEnum, getValueEnum } from "../../../Unbookable/utils";
import { OrderStatusCode, OrderStatusType } from "../../Tags/OrderStatus";
import {
  ProductCategoryStatus,
  ProductCategoryStatusType,
} from "../../Tags/ProductCategoryStatus";
import { ProductOptionStatusTag } from "../../Tags/ProductOptionStatus";
import { ProductItemCardTemplate } from "./Card/template";
import { ProductDeliveryInfoForm } from "./Delivery";
import { RepairProductItemTireProcessForm } from "./Process/Tire";
import { RepairProductItemWheelProcessForm } from "./Process/Wheel";
import { ProductItemInfoForm } from "./ProductOption";

interface RepairProductItemTireCardFormProps {
  repairNo?: string;
  dataSource?: CustomerOrderProductInfo;
}

/**
 * 매장 출고정보 - 상품주문 상세 - 타이어
 * @param param0
 * @returns
 */
export const RepairProductItemTireCardForm: React.FC<
  RepairProductItemTireCardFormProps
> = ({ repairNo, dataSource }) => {
  const { reservation } = useReservation();

  /**
   * 결제정보 조회
   * -- 환불/반품상태도 추가 조회
   */
  const { data: refundData } = useQuery(
    ["OM", reservation?.repairNo, "RefundInfo"],
    () => getOMRefundInfo(reservation?.repairNo as string),
    {
      enabled: !!reservation?.repairNo,
      refetchOnMount: "always",
    }
  );

  /**
   * 휠여부
   */
  const isWheel = useMemo(
    () => dataSource?.productCategoryName === "휠얼라인먼트",
    [dataSource]
  );

  const canceledAt = useMemo(
    () =>
      refundData?.rows.find(
        (item) => item.orderItemNo === dataSource?.orderItemNo
      )?.returnConfirmedAt || dataSource?.canceledAt,
    [refundData, dataSource]
  );

  /**
   * 스톡타입
   */
  const stockType = useMemo(
    () =>
      dataSource?.goodsIssueItem?.warehouse?.id ? "NORMAL" : "CONSIGNMENT",
    [dataSource]
  );

  /**
   * 상품주문상태 코드값
   */
  const orderCode = useMemo(() => {
    if (dataSource?.orderItemStatus) {
      const status: OrderStatusType = dataSource?.orderItemStatus;

      return getValueEnum(status, OrderStatusCode) as number;
    }

    return undefined;
  }, [dataSource]);
  return (
    <>
      <ProductItemCardTemplate orderItemNo={dataSource?.orderItemNo}>
        <CustomDescriptions
          colon={false}
          column={1}
          bordered={false}
          labelStyle={{ width: 100 }}
          layout="horizontal"
        >
          <CustomDescriptions.Item label="상품분류">
            {dataSource?.productCategoryName || "-"}
          </CustomDescriptions.Item>
          {!isWheel && (
            <CustomDescriptions.Item label="브랜드">
              {dataSource?.brand || "-"}
            </CustomDescriptions.Item>
          )}
          {!isWheel && (
            <CustomDescriptions.Item label="판매상품명">
              {dataSource?.orderItemName || "-"}
            </CustomDescriptions.Item>
          )}
          {dataSource?.productCategoryName === "타이어" && (
            <CustomDescriptions.Item label="ERP 코드" span={1}>
              {dataSource?.erpCode || "-"}
            </CustomDescriptions.Item>
          )}
          <CustomDescriptions.Item label="수량" span={1}>
            {dataSource?.orderItemAmount
              ? `${FormatFunction.numFormat(dataSource?.orderItemAmount)} 개`
              : "-"}
          </CustomDescriptions.Item>
          {!isWheel && (
            <CustomDescriptions.Item label="상품유형" span={1}>
              {(dataSource?.productOptionType && (
                <ProductOptionStatusTag value={dataSource.productOptionType} />
              )) ||
                "-"}
            </CustomDescriptions.Item>
          )}
          {dataSource?.orderItemProductOptions?.length && (
            <CustomDescriptions.Item>
              <div style={{ width: "100%" }}>
                <ProductItemInfoForm
                  dataSource={
                    dataSource?.orderItemProductOptions as OrderPackageInfo[]
                  }
                  type={
                    (dataSource?.productCategoryName &&
                      (getKeyEnum(
                        dataSource.productCategoryName,
                        ProductCategoryStatus
                      ) as ProductCategoryStatusType)) ||
                    undefined
                  }
                  showPrice={false}
                />
              </div>
            </CustomDescriptions.Item>
          )}
          <CustomDescriptions.Item>
            <div style={{ width: "100%" }}>
              {isWheel !== true && (
                <RepairProductItemTireProcessForm
                  dataSource={dataSource}
                  canceledAt={canceledAt}
                />
              )}
              {isWheel && (
                <RepairProductItemWheelProcessForm
                  dataSource={dataSource}
                  canceledAt={canceledAt}
                />
              )}
            </div>
          </CustomDescriptions.Item>
          {stockType === "CONSIGNMENT" &&
            orderCode &&
            orderCode !== 300 &&
            orderCode >= 213 &&
            ["타이어", "엔진오일", "와이퍼"].includes(
              dataSource?.productCategoryName as string
            ) && (
              <CustomDescriptions.Item>
                <div style={{ width: "100%" }}>
                  <ProductDeliveryInfoForm dataSource={dataSource} />
                </div>
              </CustomDescriptions.Item>
            )}
        </CustomDescriptions>
      </ProductItemCardTemplate>
    </>
  );
};
